import { FC, RefObject, useRef } from "react";
import { useSelector } from "react-redux";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { mapTagsToOptionsWithGrouping } from "helpers/tags";
import { useClickOutside } from "hooks/useClickOutside";
import { ITag } from "store/entities/tags";
import { selectCurrentAgentTeams } from "store/entities/teams";

interface ITagSelectProps {
  tags: ITag[];
  selected: ITag[];
  onSelect: (tag: string) => void;
  onClickOutside: () => void;
  teamID?: string;
}

export const TagSelect: FC<ITagSelectProps> = ({ tags, onSelect, selected, onClickOutside, teamID }) => {
  const optionsRef = useRef<HTMLElement | Window | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const teams = useSelector(selectCurrentAgentTeams);

  const allTagsSelected = selected.length === tags.length;
  const noTagsCreated = !tags.length;

  const tagsToSelect = (): IPickerListItem[] => {
    if (allTagsSelected || noTagsCreated) return [];

    const unselectedTags = tags.filter(tag => !selected.some(selectedTag => selectedTag.id === tag.id));

    return mapTagsToOptionsWithGrouping(unselectedTags, teams, teamID);
  };

  const noSearchResultText = (): string => {
    if (allTagsSelected) return "All tags used";
    if (noTagsCreated) return "No tags created";
    return "No tags found";
  };

  const selectTag = (selectedTags: IPickerListItem[] | null): void => {
    if (selectedTags) onSelect(selectedTags[0].key);
  };

  useClickOutside({ elementRef: [selectRef, optionsRef as RefObject<HTMLElement>], onClickOutside });

  return (
    <div ref={selectRef}>
      <Picker
        maxListHeight={210}
        options={tagsToSelect()}
        noSearchResultText={noSearchResultText()}
        onSelect={selectTag}
        hideClearButton
        placement="top"
        size="compact"
        placeholder="Search tag..."
        isVisible
        virtuosoProps={{ scrollerRef: ref => (optionsRef.current = ref) }}
        useDismissHookProps={{ escapeKey: false }}
        useClickHookProps={{ toggle: false }}
      />
    </div>
  );
};
